/*
SPDX-FileCopyrightText: 2025 Genome Research Ltd.

SPDX-License-Identifier: MIT
*/

import React from "react";
import { CentreContents } from "@tol/tol-ui";
import { Link } from "react-router-dom";
import "../../scss/legacy-tol.scss";

const LegacyConsortium: React.FC = () => {
  return (
    <div style={{ backgroundColor: "white", color: "black" }}>
      <CentreContents>
        <header className="header">
          <h1 className="header-h1">Tree of sex</h1>
          <h2 className="header-h2">
            a database of eukaryotic sex determination systems
          </h2>
          <nav className="nav">
            <Link className="link" to={"/legacy-home"}>
              Home{" "}
            </Link>
            <span>Consortium</span>
            <Link className="link" to={"/legacy-database"}>
              Database{" "}
            </Link>
            <Link className="link" to={"/legacy-publications"}>
              Publications{" "}
            </Link>
          </nav>
        </header>
        <main className="legacy-main">
          <p>
            The Tree of Sex Consortium is a{" "}
            <a className="link" href="https://nescent.org/" target="_blank">
              NESCent
            </a>{" "}
            funded working group to assemble the Tree of Sex database.
          </p>
          <div className="imgs">
            <img alt="SEXCHROMgroup" src="/legacy/sexchromgroup.jpg" />
          </div>
          <ul className="pub">
            <li>
              <a
                className="link"
                href="https://bachtroglabberkeley.wordpress.com/"
                target="_blank"
              >
                Doris Bachtrog
              </a>
              , University of California Berkeley, is an evolutionary biologist
              interested in the evolutionary and molecular mechanisms driving
              sex chromosome differentiation in flies, birds and snakes. She
              helped with the assembly of the invertebrate database.
            </li>

            <li>
              <a
                className="link"
                href="https://www.ee.iee.unibe.ch/index_eng.html"
                target="_blank"
              >
                Katie Peichel
              </a>
              , Fred Hutchinson Cancer Research Center, is an evolutionary
              biologist studying the molecular mechanisms and evolutionary
              forces driving the rapid turnover of sex chromosomes in
              stickleback fish. She coordinated the assembly of the vertebrate
              database.
            </li>

            <li>
              <a
                className="link"
                href="https://www.zoology.ubc.ca/mank-lab/"
                target="_blank"
              >
                Judith Mank
              </a>
              , University College London, is an evolutionary biologist studying
              how sex-specific selection and sexual conflict affect genome
              evolution. She helped with the assembly of the vertebrate
              database.
            </li>

            <li>
              <a
                className="link"
                href="https://www.eeob.iastate.edu/faculty/valenzuela/"
                target="_blank"
              >
                Nicole Valenzuela
              </a>
              , Iowa State University, is an evolutionary biologists interested
              in the evolution of sex determination, sex chromosomes, and genome
              organization/function in reptiles and other vertebrates. She
              helped with the assembly of the vertebrate dataset.
            </li>

            <li>
              <a
                className="link"
                href="http://coleoguy.github.io/"
                target="_blank"
              >
                Heath Blackmon
              </a>
              , Texas A&amp;M University, is an evolutionary biologist
              interested in the mode and tempo of chromosome number and sex
              chromosomes evolution. He helped with the assembly of the
              invertebrate database, and maintains these pages and curates data
              for the vertebrates and invertebrates.
            </li>

            <li>
              <a
                className="link"
                href="http://homepages.ucalgary.ca/~jvamosi/"
                target="_blank"
              >
                Jana Vamosi
              </a>
              , University of Calgary, is an evolutionary ecologist interested
              in the causes and consequences of plant biodiversity. She helped
              with the assembly of the plant database and phylogenetic analysis.
            </li>

            <li>
              <a
                className="link"
                href="http://www.zoology.ubc.ca/~otto/"
                target="_blank"
              >
                Sarah (Sally) Otto
              </a>
              , University of British Columbia, uses theoretical models, as well
              as comparative analyses and experimental tests, to explore the
              evolution of sexual systems and genomic architecture. She helped
              build the plant database.
            </li>

            <li>
              <a
                className="link"
                href="http://www.tau.ac.il/~itaymay/"
                target="_blank"
              >
                Itay Mayrose
              </a>
              , Tel-Aviv University, is a plant evolutionary biologist
              interested in whole genome duplications, plant mating systems
              evolution, and in the development of phylogenetic methodologies.
              He helped with the assembly of the plant database.
            </li>

            <li>
              <a
                className="link"
                href="https://hahnlab.sitehost.iu.edu/"
                target="_blank"
              >
                Matthew Hahn
              </a>
              , Indiana University, is interested in the evolution of genes and
              genomes, especially the differentiation of insect sex chromosomes.
              He helped with the assembly of the insect database.
            </li>

            <li>
              <a
                className="link"
                href="https://ashmanlab2012.wixsite.com/ashmanlab"
                target="_blank"
              >
                Tia-Lynn Ashman
              </a>
              , University of Pittsburgh, is an evolutionary ecologist
              interested in the evolution of sexual systems and sex chromosomes
              in flowering plants. She helped with the assembly of the
              angiosperm database.
            </li>

            <li>
              <a
                className="link"
                href="http://www.umn.edu/~eeg"
                target="_blank"
              >
                Emma Goldberg
              </a>
              , University of Minnesota, is a theorist interested in
              macroevolution, plant mating systems, and biogeography. She helped
              with the assembly and analysis of the plant database.
            </li>

            <li>
              <a className="link" href="https://mwpennell.ca/" target="_blank">
                Matt Pennell
              </a>
              , University of Idaho, is an evolutionary biologist interested in
              understanding sex chromosome evolution from a phylogenetic
              perspective. He is helping with analyses on a variety of projects.
            </li>

            <li>
              <a
                className="link"
                href="https://www.nig.ac.jp/nig/research/organization-top/organization/kitano"
                target="_blank"
              >
                Jun Kitano
              </a>
              , National Institute of Genetics, Japan, is an evolutionary
              biologist interested in the genetic mechanisms of speciation and
              the evolution of sexual dimorphism in fishes. He helped with the
              assembly of the vertebrate database.
            </li>

            <li>
              <a
                className="link"
                href="http://www.unil.ch/dee/page6788.html"
                target="_blank"
              >
                Nicolas Perrin
              </a>
              , Lausanne University, is interested in the evolution of sex
              determination and sex chromosomes, using amphibians as model
              organisms. He helped in the assembly of the vertebrate database.
            </li>

            <li>
              <a
                className="link"
                href="http://lauraross.bio.ed.ac.uk/"
                target="_blank"
              >
                Laura Ross
              </a>
              , University of Edinburgh, is an evolutionary biologist interested
              in the evolution of unusual reproductive systems across
              invertebrates. She helped with the assembly of the invertebrate
              database.
            </li>

            <li>
              <a
                className="link"
                href="https://kirkpatricklab.org/"
                target="_blank"
              >
                Mark Kirkpatrick
              </a>
              , University of Texas Austin, is a theoretical evolutionary
              biologist interested in the population forces driving the
              transition of sex determination systems.
            </li>
          </ul>
        </main>
        <footer className="legacy-footer">
          <p>
            &copy; 2014 Tree of Sex Consortium :{" "}
            <Link to={"/legacy-cookies-policy"}>Cookies Policy</Link>
          </p>
        </footer>
      </CentreContents>
    </div>
  );
};
export default LegacyConsortium;
